import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api, type LanguageOption, type VocabWord } from "../../api";
import { useProfile } from "../../layout/AppShell";
import LanguageTabs from "../../components/LanguageTabs";
import { fadeUp, stagger } from "../../lib/motion";
import { cn } from "../../lib/format";

type Filter = "all" | "due" | "mastered" | "comfortable" | "learning" | "new";
type Sort = "progress" | "a-z" | "due";

const FILTERS: { id: Filter; label: string; hint: string }[] = [
  { id: "all",         label: "All",         hint: "every word you've met" },
  { id: "mastered",    label: "Mastered",    hint: "used unaided in 5+ replies" },
  { id: "comfortable", label: "Comfortable", hint: "producing them when prompted" },
  { id: "learning",    label: "Learning",    hint: "seen 1–4 times, still wobbly" },
  { id: "due",         label: "Due",         hint: "ripe for review today" },
];

// German has six declined-article forms but the noun-detection only needs
// the three nominatives (der/die/das); declined ones land in messages
// rarely enough to ignore in this simple split. Other languages use only
// the article table from the catalog.
const GERMAN_ARTICLE_DECLENSIONS = new Set(["den", "dem", "des"]);

const GENDER_COLOR: Record<string, string> = {
  m: "#3A6EA5",
  f: "#B8472C",
  n: "#3F6A44",
};

type Bucket = "mastered" | "comfortable" | "learning";
const BUCKETS: { id: Bucket; glyph: string; label: string; sub: string }[] = [
  { id: "mastered",    glyph: "●", label: "Mastered",    sub: "used unaided in 5+ replies" },
  { id: "comfortable", glyph: "○", label: "Comfortable", sub: "producing them when prompted" },
  { id: "learning",    glyph: "◌", label: "Learning",    sub: "seen 1–4 times, still wobbly" },
];

function bucketFor(mastery: number): Bucket {
  if (mastery >= 4) return "mastered";
  if (mastery >= 2) return "comfortable";
  return "learning";
}

const LANG_NAME: Record<string, string> = {
  de: "German", es: "Spanish", fr: "French", it: "Italian",
  pt: "Portuguese", el: "Greek", pl: "Polish", ja: "Japanese",
  zh: "Chinese", ko: "Korean", nl: "Dutch", ru: "Russian", ar: "Arabic",
};
function languageName(code: string): string {
  return LANG_NAME[code?.toLowerCase()] ?? code.toUpperCase();
}

type DisplayWord = VocabWord & {
  displayWord: string;
  displayArticle: string | null;
  bucket: Bucket;
  due: boolean;
  nextDueLabel: string;
};

// Soft "next-due" hint derived from mastery bucket. The API doesn't yet
// surface a real `next_due_at`; replace this when it does. Due words are
// pulled from the dedicated /due endpoint so we know who's overdue today.
function nextDueLabelFor(bucket: Bucket, due: boolean): string {
  if (due) return "due now";
  if (bucket === "mastered") return "next: in weeks";
  if (bucket === "comfortable") return "next: in days";
  return "next: soon";
}

function prepare(
  word: VocabWord,
  lang: string,
  dueSet: Set<string>,
  articles: Set<string>,
  genderTable: Record<string, string>,
): DisplayWord {
  const raw = (word.word || "").trim();
  const parts = raw.split(/\s+/);
  let article: string | null = null;
  let core = raw;
  // Article-prefix split: drop the leading word if it's a known article
  // for this language (or a German declined form, which we keep
  // language-specific because only German declines them).
  const isPrefixArticle =
    parts.length >= 2
    && (articles.has(parts[0].toLowerCase())
        || (lang === "de" && GERMAN_ARTICLE_DECLENSIONS.has(parts[0].toLowerCase())));
  if (isPrefixArticle) {
    article = parts[0].toLowerCase();
    core = parts.slice(1).join(" ");
  } else if (word.gender && genderTable[word.gender]) {
    article = genderTable[word.gender];
  }
  const isNoun = (word.pos || "").toLowerCase() === "noun" || word.gender;
  // Noun capitalisation is German-specific orthography.
  if (lang === "de" && isNoun && core.length > 0) {
    core = core[0].toUpperCase() + core.slice(1);
  }
  const bucket = bucketFor(word.mastery);
  const due = dueSet.has(core.toLowerCase()) || dueSet.has(raw.toLowerCase());
  return {
    ...word,
    displayWord: core,
    displayArticle: article,
    bucket,
    due,
    nextDueLabel: nextDueLabelFor(bucket, due),
  };
}

function dedupe(words: DisplayWord[]): DisplayWord[] {
  const by = new Map<string, DisplayWord>();
  for (const w of words) {
    const key = w.displayWord.toLowerCase();
    const existing = by.get(key);
    if (!existing) {
      by.set(key, w);
    } else {
      const merged: DisplayWord = {
        ...existing,
        mastery: Math.max(existing.mastery, w.mastery),
        reps: Math.max(existing.reps, w.reps),
        gloss: existing.gloss || w.gloss,
        gender: existing.gender || w.gender,
        level: existing.level || w.level,
        pos: existing.pos || w.pos,
        displayArticle: existing.displayArticle || w.displayArticle,
        due: existing.due || w.due,
      };
      merged.bucket = bucketFor(merged.mastery);
      merged.nextDueLabel = nextDueLabelFor(merged.bucket, merged.due);
      by.set(key, merged);
    }
  }
  return [...by.values()];
}

export default function Vocabulary() {
  const { profile, setProfile } = useProfile();
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("progress");
  const [q, setQ] = useState("");
  const [words, setWords] = useState<VocabWord[] | null>(null);
  const [dueWords, setDueWords] = useState<VocabWord[]>([]);
  const [catalog, setCatalog] = useState<LanguageOption[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [viewLang, setViewLang] = useState<string>(profile.active_language);
  const owned = profile.languages || [profile.active_language];

  // Fetch the full set ("seen") plus the due-now subset in parallel.
  // Knowing the due IDs lets every row render a "due now" badge inline.
  useEffect(() => {
    setWords(null);
    setDueWords([]);
    setErr(null);
    Promise.all([
      api.getWords("seen", viewLang),
      api.getWords("due", viewLang).catch(() => ({ words: [] as VocabWord[] })),
    ])
      .then(([all, due]) => {
        setWords(all.words);
        setDueWords(due.words || []);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "failed"));
  }, [viewLang]);

  useEffect(() => {
    api.getLanguages().then((r) => setCatalog(r.languages || [])).catch(() => {});
  }, []);

  // Per-language article rules — pulled from the registry rather than
  // hard-coded so French/Italian/Spanish render correctly.
  const langMeta = useMemo(
    () => catalog.find((l) => l.code === viewLang),
    [catalog, viewLang],
  );
  const articles = useMemo(() => {
    const s = new Set<string>();
    Object.values(langMeta?.gender_articles ?? {}).forEach((a) => s.add(a.toLowerCase()));
    return s;
  }, [langMeta]);
  const genderTable = useMemo(
    () => (langMeta?.gender_articles ?? {}) as Record<string, string>,
    [langMeta],
  );

  const dueSet = useMemo(() => {
    const s = new Set<string>();
    for (const w of dueWords) {
      const norm = (w.word || "").trim().toLowerCase();
      if (norm) {
        s.add(norm);
        // Also add the de-articled form so prepared words match.
        const parts = norm.split(/\s+/);
        if (parts.length >= 2 && articles.has(parts[0])) {
          s.add(parts.slice(1).join(" "));
        }
      }
    }
    return s;
  }, [dueWords, articles]);

  // Prepare + dedupe the full set once. All filtering/sorting works on this.
  const allPrepared: DisplayWord[] = useMemo(() => {
    if (!words) return [];
    return dedupe(words.map((w) => prepare(w, viewLang || "de", dueSet, articles, genderTable)));
  }, [words, viewLang, dueSet, articles, genderTable]);

  // Counts per filter — for the chip labels.
  const counts = useMemo(() => {
    const c = { all: allPrepared.length, mastered: 0, comfortable: 0, learning: 0, due: 0, new: 0 };
    for (const w of allPrepared) {
      c[w.bucket]++;
      if (w.due) c.due++;
    }
    return c;
  }, [allPrepared]);

  const filtered: DisplayWord[] = useMemo(() => {
    let rows = allPrepared;
    if (filter === "due") rows = rows.filter((w) => w.due);
    else if (filter === "mastered" || filter === "comfortable" || filter === "learning") {
      rows = rows.filter((w) => w.bucket === filter);
    } else if (filter === "new") {
      rows = rows.filter((w) => w.reps <= 1);
    }
    const needle = q.trim().toLowerCase();
    if (needle) {
      rows = rows.filter(
        (w) =>
          w.displayWord.toLowerCase().includes(needle) ||
          (w.gloss || "").toLowerCase().includes(needle),
      );
    }
    if (sort === "a-z") {
      rows = [...rows].sort((a, b) => a.displayWord.localeCompare(b.displayWord));
    } else if (sort === "due") {
      rows = [...rows].sort((a, b) => {
        if (a.due !== b.due) return a.due ? -1 : 1;
        return b.mastery - a.mastery;
      });
    } else {
      // progress: highest mastery first
      rows = [...rows].sort((a, b) => b.mastery - a.mastery || a.displayWord.localeCompare(b.displayWord));
    }
    return rows;
  }, [allPrepared, filter, q, sort]);

  // For the "all" view at progress sort, group rendering by bucket.
  // For any specific filter or non-progress sort, render flat.
  const grouped = useMemo(() => {
    if (filter !== "all" || sort !== "progress") return null;
    const groups: Record<Bucket, DisplayWord[]> = { mastered: [], comfortable: [], learning: [] };
    for (const w of filtered) groups[w.bucket].push(w);
    return groups;
  }, [filter, sort, filtered]);

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      <LanguageTabs
        languages={owned}
        value={viewLang}
        active={profile.active_language}
        onChange={setViewLang}
        onMakeActive={async (code) => {
          const updated = await api.updateMe({ active_language: code });
          setProfile(updated);
        }}
      />

      <motion.div variants={fadeUp} className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="font-serif text-3xl md:text-4xl leading-tight">Words</h1>
          {words && (
            <span className="font-mono text-[12px] text-ink-faint">
              {allPrepared.length.toLocaleString()} in {languageName(viewLang)}
            </span>
          )}
        </div>
        <div className="font-mono text-[12px] text-ink-faint">
          sort by{" "}
          {(["progress", "a-z", "due"] as Sort[]).map((s, i) => (
            <span key={s}>
              {i > 0 && <span className="mx-1.5 text-ink-faint/50">·</span>}
              <button
                onClick={() => setSort(s)}
                className={cn(
                  "transition-colors inline-block py-2 -my-2",
                  sort === s
                    ? "text-ink underline decoration-ink underline-offset-2 font-medium"
                    : "text-ink-faint underline decoration-ink-faint/40 hover:text-ink hover:decoration-ink",
                )}
              >
                {s}
              </button>
            </span>
          ))}
        </div>
      </motion.div>

      {/* Search */}
      <motion.div variants={fadeUp} className="mt-6 relative">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-faint" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by word or meaning…"
          className="w-full bg-paper border border-[rgba(42,36,26,0.18)] rounded-md pl-10 pr-4 py-3 text-[14px] focus:outline-none focus:border-terracotta"
        />
      </motion.div>

      {/* Filter chips with counts */}
      <motion.div variants={fadeUp} className="mt-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const n = counts[f.id as keyof typeof counts] ?? 0;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              title={f.hint}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 transition-colors",
                active
                  ? "border-ink bg-ink text-paper"
                  : "border-[rgba(42,36,26,0.18)] text-ink-soft hover:border-terracotta hover:text-terracotta",
              )}
            >
              <span className="font-sans text-[13px]">{f.label}</span>
              {words && (
                <span
                  className={cn(
                    "font-mono text-[10px]",
                    active ? "text-paper/70" : "text-ink-faint",
                  )}
                >
                  · {n}
                </span>
              )}
            </button>
          );
        })}
      </motion.div>

      {err && <div className="mt-6 text-terracotta text-sm">{err}</div>}

      {/* Loading skeleton */}
      {!words && (
        <div className="mt-10 space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-12 rounded-md bg-paper-soft/60 animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      )}

      {/* Empty state — design copy */}
      {words && filtered.length === 0 && (
        <div className="mt-10 border-t border-[rgba(42,36,26,0.12)] pt-10 max-w-xl">
          <h2 className="font-serif text-2xl">
            {filter === "all"
              ? "No words yet — that's how it starts."
              : `Nothing in ${FILTERS.find((f) => f.id === filter)?.label.toLowerCase() ?? "this view"} yet.`}
          </h2>
          <p className="mt-3 font-serif italic text-ink-soft">
            {filter === "all"
              ? "Send your tutor a “hi” in Telegram. The first word lands here within a sentence or two."
              : "When your tutor next slips a word in that qualifies, it'll show up here."}
          </p>
          {filter === "all" && (
            <a
              href={`https://t.me/${import.meta.env.VITE_BOT_USERNAME || "ilmli_bot"}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-md border border-ink/80 px-4 py-2 text-[13px] hover:bg-ink hover:text-paper transition-colors"
            >
              Open Telegram →
            </a>
          )}
        </div>
      )}

      {/* Grouped view */}
      {words && filtered.length > 0 && grouped && (
        <motion.div variants={fadeUp} className="mt-10 space-y-12">
          {BUCKETS.map((b) => {
            const rows = grouped[b.id];
            if (rows.length === 0) return null;
            return (
              <section key={b.id}>
                <div className="flex items-baseline justify-between gap-4 mb-3">
                  <div className="flex items-baseline gap-3">
                    <span
                      className="font-serif text-xl leading-none"
                      style={{
                        color:
                          b.id === "mastered"
                            ? "var(--ink-soft)"
                            : b.id === "comfortable"
                              ? "var(--terracotta)"
                              : "var(--ink-faint)",
                      }}
                      aria-hidden
                    >
                      {b.glyph}
                    </span>
                    <h2 className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-soft">
                      {b.label} · {rows.length}
                    </h2>
                  </div>
                  <span className="font-mono text-[11px] italic text-ink-faint">
                    {b.sub}
                  </span>
                </div>
                <WordList rows={rows} />
              </section>
            );
          })}
        </motion.div>
      )}

      {/* Flat view */}
      {words && filtered.length > 0 && !grouped && (
        <motion.div variants={fadeUp} className="mt-8">
          <WordList rows={filtered} />
        </motion.div>
      )}
    </motion.div>
  );
}

function WordList({ rows }: { rows: DisplayWord[] }) {
  return (
    <ul className="divide-y divide-[rgba(42,36,26,0.10)] border-y border-[rgba(42,36,26,0.10)]">
      {rows.map((w, i) => (
        <li
          key={`${w.displayWord}-${i}`}
          className={cn(
            "flex items-baseline gap-4 px-3 py-3.5 transition-colors",
            w.due
              ? "bg-terracotta-tint hover:bg-terracotta-tint"
              : "hover:bg-paper-soft/60",
          )}
        >
          {w.displayArticle && (
            <span
              className="font-serif italic text-base shrink-0 w-10"
              style={{ color: w.gender ? GENDER_COLOR[w.gender] : "var(--ink-faint)" }}
            >
              {w.displayArticle}
            </span>
          )}
          {!w.displayArticle && <span className="w-10 shrink-0" aria-hidden />}
          <div className="flex-1 min-w-0 flex items-baseline gap-3 flex-wrap">
            <span className="font-serif text-xl leading-tight">{w.displayWord}</span>
            {w.gloss && (
              <span className="font-serif italic text-ink-soft text-[14px]">
                {w.gloss}
              </span>
            )}
            {w.pos && (
              <span className="font-serif italic text-ink-faint text-[12px]">
                · {w.pos}
              </span>
            )}
            {w.level && (
              <span className="font-mono text-[10px] tracking-wide text-ink-faint border border-[rgba(42,36,26,0.15)] rounded-full px-1.5 py-px">
                {w.level}
              </span>
            )}
          </div>
          <span
            className={cn(
              "font-mono text-[11px] shrink-0",
              w.due ? "text-terracotta font-medium" : "text-ink-faint",
            )}
          >
            {w.nextDueLabel}
          </span>
        </li>
      ))}
    </ul>
  );
}
